import { CheckboxGroup } from '@app/components/various/CheckboxGroup';
import { DelwinNameAndDate } from '@app/components/various/DelwinNameAndDate';
import { getDeliveryWindowsGroupsIds } from '@app/ducks';
import { isDeliveryWindowFilterGroup } from '@app/logic/deliveryWindows';
import { useCheckboxGroupState } from '@app/utils/hooks/checkboxGroup/useCheckboxGroupState';
import { useDeliveryWindowFilterItems } from '@app/utils/hooks/filtersBar';
import { isDefined } from '@app/utils/is';
import { Checkboxes } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

interface Props {
  selectedOptions: string[];
  onSelectedOptionsChange: (selectedValues: string[]) => void;
  stockType?: string;
}

export const FiltersDeliveryWindows = ({ selectedOptions, onSelectedOptionsChange, stockType }: Props) => {
  const delwinGroupsIds = useSelector(getDeliveryWindowsGroupsIds);
  const items = useDeliveryWindowFilterItems({ stockType });
  const options = React.useMemo(
    () =>
      items.reduce((acc: Checkboxes.Option[], deliveryWindowFilterItem) => {
        const isGroup = isDeliveryWindowFilterGroup(deliveryWindowFilterItem);

        if (isGroup) {
          const childItems = deliveryWindowFilterItem.items.map(deliveryWindow => ({
            name: <DelwinNameAndDate deliveryWindow={deliveryWindow} shouldStretch />,
            parentValue: deliveryWindowFilterItem.groupId,
            value: deliveryWindow.deliveryWindow,
          }));

          return [
            ...acc,
            {
              name: deliveryWindowFilterItem.name,
              value: deliveryWindowFilterItem.groupId,
            },
            ...childItems,
          ];
        }

        return [
          ...acc,
          {
            name: <DelwinNameAndDate deliveryWindow={deliveryWindowFilterItem} shouldStretch />,
            value: deliveryWindowFilterItem.deliveryWindow,
          },
        ];
      }, []),
    [items],
  );

  const setSelectedFilters = React.useCallback(
    (selectedValues: string[]) => {
      const filteredSelectedValues = selectedValues.filter(value => !delwinGroupsIds.includes(value));
      onSelectedOptionsChange(filteredSelectedValues);
    },
    [delwinGroupsIds, onSelectedOptionsChange],
  );

  const { batchUpdate, optionsState, toggleOptionState } = useCheckboxGroupState({
    onOptionToggle: setSelectedFilters,
    options,
  });

  React.useEffect(() => {
    if (isDefined(selectedOptions) && Array.isArray(selectedOptions)) {
      batchUpdate(selectedOptions);
    }
  }, [selectedOptions, batchUpdate]);

  if (optionsState === null) {
    return null;
  }

  return <CheckboxGroup options={options} optionsState={optionsState} onChange={toggleOptionState} collapseLevel={1} />;
};
